import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../services/api';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('register'); // register | verify
  const [verifyEmail, setVerifyEmail] = useState('');
  const location = useLocation();
  const from = location.state?.from || '/ask';

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.register(email, password, name);
      if (res.requiresVerification) {
        setVerifyEmail(res.email);
        setStep('verify');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.verifyEmail(verifyEmail, code);
      localStorage.setItem('auth_token', data.token);
      // Reload user in auth context
      window.location.href = from;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      const data = await api.resendCode(verifyEmail);
      setError(''); // clear any old error
      alert(data.message);
    } catch (err) {
      setError(err.message);
    }
  };

  if (step === 'verify') {
    return (
      <div className="min-h-screen pt-24 px-6 flex justify-center">
        <div className="w-full max-w-sm">
          <h1 className="font-serif text-2xl text-warm text-center mb-2">Verify Your Email</h1>
          <p className="text-warm-100/50 text-sm text-center mb-8">
            We sent a 6-digit code to <span className="text-warm">{verifyEmail}</span>
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-6 text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-warm-100/60 text-xs mb-1.5">Verification Code</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                className="w-full bg-dark-100 border border-dark-200/50 rounded-lg px-4 py-3 text-warm text-2xl text-center tracking-[0.5em] font-mono focus:outline-none focus:border-gold/30 placeholder-warm-100/30"
                placeholder="000000"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full bg-gold/15 border border-gold/30 text-gold py-3 rounded-lg text-sm hover:bg-gold/25 transition-colors disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify & Continue'}
            </button>
          </form>

          <p className="text-warm-100/40 text-sm text-center mt-6">
            Didn't receive the code?{' '}
            <button onClick={handleResend} className="text-gold hover:text-gold-light transition-colors">
              Resend
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-6 flex justify-center">
      <div className="w-full max-w-sm">
        <h1 className="font-serif text-2xl text-warm text-center mb-2">Create Account</h1>
        <p className="text-warm-100/50 text-sm text-center mb-8">Start with 3 free questions per day</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-6 text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label className="block text-warm-100/60 text-xs mb-1.5">Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full bg-dark-100 border border-dark-200/50 rounded-lg px-4 py-3 text-warm text-sm focus:outline-none focus:border-gold/30 placeholder-warm-100/30"
              placeholder="How should we address you?"
            />
          </div>
          <div>
            <label className="block text-warm-100/60 text-xs mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full bg-dark-100 border border-dark-200/50 rounded-lg px-4 py-3 text-warm text-sm focus:outline-none focus:border-gold/30 placeholder-warm-100/30"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block text-warm-100/60 text-xs mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full bg-dark-100 border border-dark-200/50 rounded-lg px-4 py-3 text-warm text-sm focus:outline-none focus:border-gold/30 placeholder-warm-100/30"
              placeholder="Min 8 characters"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gold/15 border border-gold/30 text-gold py-3 rounded-lg text-sm hover:bg-gold/25 transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-warm-100/40 text-sm text-center mt-6">
          Already have an account?{' '}
          <Link to="/login" state={{ from }} className="text-gold hover:text-gold-light transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
